import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import { useOverlays } from "contexts/OverlayContext";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useClientBulkSelection } from "./ClientBulkSelection/useClientBulkSelection";

export function useClientBulkCommandActions() {
    const {
        open: openCommands,
        close,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const bulkSelection = useClientBulkSelection();
    const activeOrganization = useActiveOrganization();
    const { openOverlay } = useOverlays();

    const [showMemo, setShowMemo] = useState(false);
    const [showServices, setShowServices] = useState(false);

    const hasSelectedWorkspaces = useMemo(
        () => bulkSelection.selected.some((item) => item.type === "workspace"),
        [bulkSelection.selected],
    );

    const handleMemoAction = useCallback(() => {
        close();
        setShowMemo(true);
    }, [close]);

    const handleTeamAction = useCallback(() => {
        const selectedWorkspaceIds = bulkSelection.selected
            .filter((item) => item.type === "workspace")
            .map((item) => item.id);

        if (selectedWorkspaceIds.length > 0 && activeOrganization) {
            close();
            openOverlay("organizationAddTeamMember", {
                organization: activeOrganization,
                initialWorkspaceIds: selectedWorkspaceIds,
                onInvited: () => {
                    bulkSelection.clearSelection();
                },
            });
        }
    }, [activeOrganization, bulkSelection, close, openOverlay]);

    const handleServicesAction = useCallback(() => {
        close();
        setShowServices(true);
    }, [close]);

    const handleOpenBulkActions = useCallback(() => {
        openCommands(CommandsDirectory.CLIENT_BULK, {
            onClientBulkMemo: handleMemoAction,
            onClientBulkTeam: hasSelectedWorkspaces
                ? handleTeamAction
                : undefined,
            onClientBulkServices: handleServicesAction,
            selectedClientCount: bulkSelection.selectedCount,
        });
    }, [
        openCommands,
        handleMemoAction,
        handleTeamAction,
        handleServicesAction,
        hasSelectedWorkspaces,
        bulkSelection.selectedCount,
    ]);

    const handleOpenRef = useRef(handleOpenBulkActions);
    handleOpenRef.current = handleOpenBulkActions;

    const hasSelection = bulkSelection.selectedCount > 0;

    useEffect(() => {
        if (hasSelection) {
            registerOpenOverride(() => handleOpenRef.current());
            return () => unregisterOpenOverride();
        }
    }, [hasSelection, registerOpenOverride, unregisterOpenOverride]);

    const closeMemo = useCallback(() => setShowMemo(false), []);
    const closeServices = useCallback(() => setShowServices(false), []);

    return {
        showMemo,
        closeMemo,
        showServices,
        closeServices,
        handleOpenBulkActions,
    };
}
